import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CustomerDTO } from '../domain/models';

export type CustomerLookupProps = {
  onSelect: (customer: CustomerDTO) => void;
  customers?: CustomerDTO[];
  placeholder?: string;
};

function displayName(c: CustomerDTO): string {
  const name = [c.firstname, c.lastname].filter(Boolean).join(' ');
  return name || c.mail || c.customerNumber || 'Unbekannt';
}

export function CustomerLookup({ onSelect, customers, placeholder }: CustomerLookupProps) {
  const data = React.useMemo(() => {
    const arr = customers || [];
    // einfache Duplikatserkennung anhand id oder email+customerNumber
    const map = new Map<string | number, CustomerDTO>();
    for (const c of arr) {
      const key = c.id ?? (c.mail || c.customerNumber || `${c.firstname}-${c.lastname}`);
      if (!map.has(key)) map.set(key, c);
    }
    return Array.from(map.values());
  }, [customers]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  // Filter ab dem ersten Buchstaben (case-insensitive, beginnt-mit bevorzugt)
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data.slice(0, 10);
    const starts = data.filter((c) =>
      [c.firstname, c.lastname, c.customerNumber, c.mail]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().startsWith(q))
    );
    const contains = data.filter((c) =>
      [c.firstname, c.lastname, c.customerNumber, c.mail]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
    // Starts-with Ergebnisse zuerst, dann die restlichen (ohne Duplikate)
    const out: CustomerDTO[] = [];
    const seen = new Set<number | string>();
    for (const c of [...starts, ...contains]) {
      const key = c.id ?? `${c.firstname}-${c.lastname}-${c.customerNumber}-${c.mail}`;
      if (!seen.has(key)) {
        seen.add(key);
        out.push(c);
      }
    }
    return out.slice(0, 20);
  }, [data, query]);

  // Klick außerhalb schließt Dropdown
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const choose = (c: CustomerDTO) => {
    onSelect(c);
    setQuery(displayName(c));
    setOpen(false);
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (!open && (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete')) {
      setOpen(true);
    }
    if (!filtered.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(filtered.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(filtered[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className="bg-white rounded-lg shadow p-4 relative">
      <label className="block text-xs font-medium text-slate-600 mb-1">Kunde suchen</label>
      <input
        className="border border-slate-300 rounded px-3 py-2 w-full bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
        placeholder={placeholder || 'Name, Kundennummer oder E-Mail eingeben'}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />

      {open && (
        <div className="absolute left-4 right-4 z-10 mt-1 bg-white border rounded shadow max-h-60 overflow-auto">
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-sm text-slate-500">Keine Treffer</div>
          )}
          {filtered.map((c, idx) => (
            <button
              key={(c.id ?? idx) as React.Key}
              type="button"
              className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${idx === highlight ? 'bg-blue-50' : ''}`}
              onMouseEnter={() => setHighlight(idx)}
              onClick={() => choose(c)}
            >
              <div className="font-medium">{displayName(c)}</div>
              <div className="text-xs text-slate-500">
                {[
                  c.customerNumber ? `#${c.customerNumber}` : null,
                  c.mail || null,
                  [c.postcode, c.city].filter(Boolean).join(' ') || null,
                ].filter(Boolean).join(' · ')}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
